import React, { useState } from 'react';
import { BLOG_POSTS } from '../blogData';
import { ArrowRight, Calendar, User, Search, Filter } from 'lucide-react';

interface BlogPageProps {
  onNavigate: (view: any, params?: any) => void;
}

export default function BlogPage({ onNavigate }: BlogPageProps) {
  const categories = ['All', ...Array.from(new Set(BLOG_POSTS.map(p => p.category)))];
  const [activeCategory, setActiveCategory] = useState('All');
  
  const filteredPosts = activeCategory === 'All' 
    ? BLOG_POSTS 
    : BLOG_POSTS.filter(p => p.category === activeCategory);

  const featuredPost = BLOG_POSTS[0];
  const otherPosts = filteredPosts.filter(p => p.id !== featuredPost.id);

  return (
    <div className="bg-stone-50 min-h-screen pt-40 pb-32 overflow-hidden relative">
      {/* Background Watermark */}
      <div className="absolute top-20 right-0 select-none pointer-events-none opacity-[0.03]">
        <span className="font-serif text-[40vw] leading-none text-mier -mr-20">MIER.</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Breadcrumbs (SEO Optimized) */}
        <nav aria-label="Breadcrumb" className="mb-12 fade-in-up">
          <ol className="flex items-center gap-3 text-[9px] uppercase tracking-[0.2em] font-bold">
            <li>
              <button onClick={() => onNavigate('home')} className="text-mier/40 hover:text-mier transition-colors">Home</button>
            </li>
            <li className="text-mier/20">/</li>
            <li>
              <span className="text-mier" aria-current="page">Journal</span>
            </li>
          </ol>
        </nav>

        <header className="mb-20 fade-in-up">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-mier/30"></div>
            <span className="text-[10px] uppercase tracking-[0.4em] text-mier-light font-medium">Lounge Journal</span>
          </div>
          <h1 className="font-serif text-6xl md:text-8xl text-mier font-light mb-8 leading-tight">
            Beauty <br /><span className="italic ml-12">Insights</span>
          </h1>
          <p className="text-zinc-500 max-w-2xl text-lg md:text-xl font-light leading-relaxed">
            A curated collection of expertise, trends, and refined guides from the masters at MIER Beauty Lounge.
          </p>
        </header>

        {/* Category Navigation */}
        <div className="flex items-center gap-8 mb-16 border-b border-mier/10 pb-8 overflow-x-auto no-scrollbar fade-in-up">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`text-[10px] uppercase tracking-[0.2em] transition-all duration-300 whitespace-nowrap ${
                activeCategory === cat 
                  ? 'text-mier font-bold border-b-2 border-mier pb-8 -mb-8' 
                  : 'text-mier-light hover:text-mier'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Featured Post */}
        {activeCategory === 'All' && featuredPost && (
          <section 
            className="group relative mb-32 cursor-pointer fade-in-up"
            onClick={() => onNavigate('blog-post', { slug: featuredPost.slug })}
          >
            <div className="grid lg:grid-cols-2 gap-0 bg-white rounded-3xl overflow-hidden shadow-2xl border border-mier/5">
              <div className="relative h-[400px] lg:h-auto overflow-hidden">
                <img 
                  src={featuredPost.image} 
                  alt={featuredPost.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-mier-dark/10 group-hover:bg-transparent transition-colors duration-500"></div>
              </div>
              <div className="p-12 md:p-20 flex flex-col justify-center">
                <span className="text-[9px] uppercase tracking-[0.3em] text-mier-light mb-8 block font-bold">Featured Article — {featuredPost.category}</span>
                <h2 className="font-serif text-4xl md:text-5xl text-mier mb-8 leading-tight group-hover:text-mier-light transition-colors">
                  {featuredPost.title}
                </h2>
                <p className="text-zinc-500 text-lg font-light leading-relaxed mb-10 line-clamp-3">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center gap-12 pt-8 border-t border-mier/5">
                   <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-mier/50 font-bold">
                      <Calendar size={14} className="text-mier-light" /> {featuredPost.date}
                   </div>
                   <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-mier group-hover:translate-x-2 transition-transform">
                      Read Full Story <ArrowRight size={14} />
                   </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-16 lg:gap-24">
          {(activeCategory === 'All' ? otherPosts : filteredPosts).map((post) => (
            <article 
              key={post.id}
              className="group cursor-pointer fade-in-up"
              onClick={() => onNavigate('blog-post', { slug: post.slug })}
            >
              <div className="relative aspect-[16/10] rounded-2xl overflow-hidden mb-10 shadow-lg border border-mier/5">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                />
                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-md text-mier text-[9px] font-bold px-4 py-1.5 rounded-full uppercase tracking-wider shadow-sm">
                  {post.category}
                </div>
              </div>
              
              <div className="px-2">
                <div className="flex items-center gap-6 text-[9px] uppercase tracking-widest text-mier-light mb-6 font-bold">
                  <span className="flex items-center gap-2">
                    <Calendar size={12} />
                    {post.date}
                  </span>
                  <span className="flex items-center gap-2">
                    <User size={12} />
                    {post.author}
                  </span>
                </div>
                
                <h3 className="font-serif text-3xl text-mier mb-6 group-hover:text-mier-light transition-colors line-clamp-2 leading-tight">
                  {post.title}
                </h3>
                
                <p className="text-zinc-500 mb-8 line-clamp-3 text-base font-light leading-relaxed">
                  {post.excerpt}
                </p>
                
                <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-mier-light group-hover:text-mier group-hover:gap-5 transition-all">
                  Full Journal Entry <ArrowRight size={14} />
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Newsletter Section */}
        <section className="mt-40 p-12 md:p-24 bg-white border border-mier/10 rounded-3xl text-center shadow-sm relative overflow-hidden fade-in-up">
          <div className="absolute top-0 left-0 w-full h-1 bg-mier"></div>
          <h2 className="font-serif text-4xl md:text-5xl text-mier mb-6 font-light italic">Join Our Private List</h2>
          <p className="text-zinc-500 mb-12 max-w-xl mx-auto text-lg font-light leading-relaxed">
            Receive refined beauty wisdom and exclusive invitations directly to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
            <input 
              type="email" 
              placeholder="Email Address"
              className="flex-1 bg-stone-50 border border-stone-200 px-8 py-5 rounded-sm focus:outline-none focus:border-mier transition-colors text-sm font-light"
            />
            <button className="bg-mier text-stone-50 px-12 py-5 rounded-sm text-xs tracking-[0.2em] uppercase font-bold hover:bg-mier-dark transition-all shadow-lg">
              Subscribe
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
